import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {
  Control,
  Controller,
  FieldArrayWithId,
  UseFormSetValue,
} from 'react-hook-form';
import { useState } from 'react';
import { GPanelPermissionsModel } from '../../../../types';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Check from '@mui/icons-material/Check';

interface DriveExpirationChipProps {
  control: Control<{
    aclResources: GPanelPermissionsModel[];
  }>;
  watchedAcl: GPanelPermissionsModel;
  acl: FieldArrayWithId<
    {
      aclResources: GPanelPermissionsModel[];
    },
    'aclResources',
    'id'
  >;
  setValue: UseFormSetValue<{
    aclResources: GPanelPermissionsModel[];
  }>;
  index: number;
}

export default function DriveExpirationChip({
  watchedAcl,
  acl,
  index,
  control,
  setValue,
}: DriveExpirationChipProps) {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [openElem, setOpenElem] = useState<GPanelPermissionsModel | null>(null);

  const isInherited = watchedAcl.permissionDetails?.some(
    (detail) => detail.inherited
  );

  const handleClick =
    (elem: GPanelPermissionsModel) =>
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      setAnchorEl(event.currentTarget);
      setOpenElem(elem);
    };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setOpenElem(null);
  };

  return (
    <>
      {watchedAcl.role === 'owner' ? (
        <Box height={19.5}></Box>
      ) : watchedAcl.permExpirationTime &&
        watchedAcl.permExpirationTime !== 0 ? (
        <Chip
          label={`Access expires ${dayjs(watchedAcl.permExpirationTime).format(
            'MMM D, h:m A'
          )}`}
          size="small"
          sx={{
            width: 'fit-content',
            position: 'relative',
            left: 70,
            bottom: 10,
          }}
          onClick={handleClick(acl)}
          color="primary"
          aria-controls={
            openElem === acl ? `date-picker-menu-${index}` : undefined
          }
          aria-haspopup="true"
          aria-expanded={openElem === acl ? 'true' : undefined}
          onDelete={() => {
            setValue(`aclResources.${index}.permExpirationTime`, 0, {
              shouldDirty: true,
            });
            if (watchedAcl.changeType !== 'add') {
              setValue(`aclResources.${index}.changeType`, 'change', {
                shouldDirty: true,
              });
            }
          }}
        />
      ) : !isInherited ? (
        <Chip
          label="Click to set expiration"
          size="small"
          sx={{
            width: 'fit-content',
            position: 'relative',
            left: 70,
            bottom: 10,
          }}
          onClick={handleClick(acl)}
          aria-controls={
            openElem === acl ? `date-picker-menu-${acl.id}` : undefined
          }
          aria-haspopup="true"
          aria-expanded={openElem === acl ? 'true' : undefined}
        />
      ) : (
        <Box height={19.5}></Box>
      )}
      <Menu
        id={`date-picker-menu-${acl.id}`}
        anchorEl={anchorEl}
        open={openElem === acl}
        onClose={handleMenuClose}
      >
        <Stack sx={{ p: 1 }} flexDirection="row" gap={1}>
          <Controller
            key={`aclResources.${index}.permExpirationTime`}
            name={`aclResources.${index}.permExpirationTime`}
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <DateTimePicker
                  label="Start Date"
                  views={['year', 'month', 'day', 'hours', 'minutes']}
                  timeSteps={{
                    minutes: 1,
                  }}
                  sx={{ width: 220 }}
                  value={
                    // one month from now at 11:59pm
                    value === 0
                      ? dayjs().add(1, 'M').endOf('day')
                      : dayjs(value)
                  }
                  disablePast
                  maxDate={dayjs().add(1, 'y')}
                  onChange={(e) => {
                    if (e) {
                      onChange(e.toDate().getTime());

                      if (watchedAcl.changeType !== 'add') {
                        setValue(`aclResources.${index}.changeType`, 'change', {
                          shouldDirty: true,
                        });
                      }
                    }
                  }}
                  onAccept={() => handleMenuClose()}
                />
              );
            }}
          />
          <IconButton
            onClick={() => {
              if (watchedAcl.permExpirationTime === 0) {
                setValue(
                  `aclResources.${index}.permExpirationTime`,
                  dayjs().add(1, 'M').endOf('day').valueOf(),
                  { shouldDirty: true }
                );
              }

              if (watchedAcl.changeType !== 'add') {
                setValue(`aclResources.${index}.changeType`, 'change', {
                  shouldDirty: true,
                });
              }

              handleMenuClose();
            }}
          >
            <Check />
          </IconButton>
        </Stack>
      </Menu>
    </>
  );
}
